/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useEffect, useState } from "react";
import { Typography } from "@mui/material";

/**
 * Props for CallTimer component.
 * @interface CallTimerProps
 * @property {boolean} isCallActive - Flag indicating if call is active
 * @property {object} [sx] - Additional Material-UI sx styling
 */
interface CallTimerProps {
  isCallActive: boolean;
  sx?: object;
}

/**
 * Timer component displaying elapsed call duration in HH:MM:SS format.
 * Automatically starts when call becomes active and resets when inactive.
 * 
 * Features:
 * - Real-time duration tracking
 * - Monospace font display
 * - HH:MM:SS format
 * - Auto-start/stop based on call state
 * - Dark overlay background
 * - Customizable styling via sx prop
 * 
 * Timer Behavior:
 * - Starts counting when isCallActive becomes true
 * - Updates every second
 * - Resets to 00:00:00 when call ends
 * - Maintains accurate duration even during re-renders
 * 
 * @param isCallActive - Whether call is currently active
 * @param sx - Additional styling overrides
 * @returns Formatted call duration display
 */
export const CallTimer = ({ isCallActive, sx = {} }: CallTimerProps) => {
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [duration, setDuration] = useState('00:00:00');

  useEffect(() => {
    if (isCallActive && !startTime) {
      setStartTime(new Date());
    } else if (!isCallActive && startTime) {
      setStartTime(null);
      setDuration('00:00:00');
    }
  }, [isCallActive, startTime]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isCallActive && startTime) {
      interval = setInterval(() => {
        const now = new Date();
        const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        
        setDuration(
          `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCallActive, startTime]);

  return (
    <Typography
      variant="body2"
      className="call-timer"
    >
      {duration}
    </Typography>
  );
};